/**
 * Usage detail drawer — what a Vercel meter actually consumed in one week.
 *
 * Vercel's billing API is per project, so there are no resources to drill into the way
 * AWS and GCP do; the equivalent question is "how much did this meter consume, and where".
 * Deliberately a separate component from `ResourceDrawer`: that one's columns (and its
 * `FinopsResourceRow` type) are resource-shaped, and overloading it would blur both.
 *
 * Rows with consumption but no cost are kept on purpose — that is the "used, but within
 * the plan's included allotment" case this view exists to show.
 */

import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import type { FinopsUsageRow } from '../../../../shared/types';
import { EmptyState, LoaderBlock, fmtNum, fmtUSDCents } from './shared';

/**
 * Meters span ten orders of magnitude — 0.42 GB of blob storage and 1.2M function
 * invocations both have to read well, so precision follows the magnitude. The unit is
 * omitted when Vercel sends none (the `Pro` plan line carries no `consumed_unit`).
 */
const fmtQuantity = (n: number, unit: string | null) => {
  let value: string;
  if (n >= 100) value = fmtNum(n);
  // Below this, two decimals collapse to "0" — reading as no consumption at all, which is
  // the opposite of what these rows document (they are kept precisely because they consumed
  // something, within the included allotment). Exact zero still reads as zero.
  else if (n > 0 && n < 0.005) value = '<0.01';
  else value = n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return unit ? `${value} ${unit}` : value;
};

export function UsageDrawer({
  open,
  onOpenChange,
  service,
  periodLabel,
  rows,
  isLoading,
  entityColLabel,
  emptyDescription,
  filterLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: string;
  periodLabel?: string;
  rows: FinopsUsageRow[];
  isLoading?: boolean;
  entityColLabel: string;
  emptyDescription?: string;
  /** Active project filter, appended to the subtitle. */
  filterLabel?: string;
}) {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{service ?? t('finops.vercel.usage_title')}</SheetTitle>
          <SheetDescription>
            {t('finops.vercel.usage_subtitle', { period: periodLabel ?? '—' })}
            {filterLabel ? ` · ${filterLabel}` : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <LoaderBlock />
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('finops.cloud.resources_empty_title')}
              description={emptyDescription ?? t('finops.vercel.usage_empty_description')}
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3">{entityColLabel}</th>
                  <th className="pr-3 text-right">{t('finops.vercel.col_quantity')}</th>
                  <th className="text-right">{t('finops.cloud.col_cost')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.project}-${r.service}`} className="border-b last:border-0">
                    <td className="py-2 pr-3">{r.project}</td>
                    <td className="pr-3 text-right tabular-nums whitespace-nowrap">
                      {fmtQuantity(r.quantity, r.unit)}
                    </td>
                    <td className="text-right tabular-nums whitespace-nowrap">{fmtUSDCents(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t px-6 py-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Gauge className="h-3.5 w-3.5 shrink-0" />
          {t('finops.vercel.usage_included_note')}
        </div>
      </SheetContent>
    </Sheet>
  );
}
